import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Trash2, Phone, Mail, UserCheck, CheckCircle2, XCircle, PhoneCall,
  ClipboardList, Pencil, StickyNote, Ban, RotateCcw, Plus, ChevronDown, ChevronUp,
  PhoneOutgoing, PhoneIncoming, MessageSquare, User, Languages,
} from "lucide-react";
import { toast } from "sonner";
import apiClient from "../../services/api";
import { formatPhone, phoneHref } from "../../lib/formatters";

const QUESTION_LABELS = [
  ["reliable_transportation", "Reliable transportation/insurance"],
  ["background_check", "Can pass Ft. Hood background check"],
  ["acknowledge_manual_labor", "Acknowledges outside/manual labor"],
  ["legally_authorized", "Legally authorized to work in U.S."],
  ["consent_contact", "Consents to Text/SMS & calls"],
];

const CONTACT_TYPES = [
  { value: "call", label: "Call" },
  { value: "text", label: "Text" },
  { value: "email", label: "Email" },
  { value: "in_person", label: "In-person" },
];

const typeLabel = (t) => (CONTACT_TYPES.find((x) => x.value === t)?.label || t);

const YesNoBadge = ({ value }) =>
  value === null || value === undefined ? (
    <span className="inline-flex items-center gap-1 text-slate-400 text-xs font-medium">— Not asked</span>
  ) : value ? (
    <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-medium">
      <CheckCircle2 className="w-3.5 h-3.5" /> Yes
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-red-600 text-xs font-medium">
      <XCircle className="w-3.5 h-3.5" /> No
    </span>
  );

const nowLocal = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const ApplicantCard = ({ app: a, onChanged, onRemoved, onEdit }) => {
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [contactForm, setContactForm] = useState({ direction: "outbound", type: "call", at: nowLocal(), note: "" });
  const [addingContact, setAddingContact] = useState(false);
  const [passOpen, setPassOpen] = useState(false);
  const [passReason, setPassReason] = useState("");
  const [passing, setPassing] = useState(false);

  const notes = a.notes || [];
  const contactLog = a.contact_log || [];

  const handleDelete = async () => {
    try {
      await apiClient.delete(`/applications/${a.id}`);
      onRemoved(a.id);
      toast.success("Application deleted");
    } catch {
      toast.error("Could not delete application");
    } finally {
      setConfirmDelete(false);
    }
  };

  const handleToggleContacted = async () => {
    const next = !a.contacted;
    try {
      const { data } = await apiClient.patch(`/applications/${a.id}/contact`, { contacted: next });
      onChanged(a.id, { contacted: data.contacted, contacted_by: data.contacted_by, contacted_at: data.contacted_at });
      toast.success(next ? "Marked as contacted" : "Marked as not contacted");
    } catch {
      toast.error("Could not update status");
    }
  };

  const handleHire = async () => {
    try {
      const { data } = await apiClient.post(`/onboarding/hire/${a.id}`);
      onChanged(a.id, {
        onboarding_status: "hired",
        hired_by: a.hired_by || "you",
        _ob: { id: data.onboarding_id, token: data.token, status: "hired", application_id: a.id },
      });
      toast.success("Applicant hired — onboarding link emailed to them");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start onboarding");
    }
  };

  const emailApplicantLink = async () => {
    try {
      const { data } = await apiClient.post(`/onboarding/${a._ob.id}/resend-invite`);
      toast.success(`Onboarding link emailed to ${data.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send email");
    }
  };

  const handleAddNote = async () => {
    const text = noteText.trim();
    if (!text) return;
    setAddingNote(true);
    try {
      const { data } = await apiClient.post(`/applications/${a.id}/notes`, { text });
      onChanged(a.id, { notes: [...notes, data.note] });
      setNoteText("");
      toast.success("Note added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add note");
    } finally {
      setAddingNote(false);
    }
  };

  const handleAddContact = async () => {
    setAddingContact(true);
    try {
      const payload = {
        direction: contactForm.direction,
        type: contactForm.type,
        at: contactForm.at ? new Date(contactForm.at).toISOString() : null,
        note: contactForm.note.trim(),
      };
      const { data } = await apiClient.post(`/applications/${a.id}/contact-log`, payload);
      onChanged(a.id, {
        contact_log: [...contactLog, data.entry],
        contacted: true,
        contacted_by: data.contacted_by,
        contacted_at: data.contacted_at,
      });
      setContactForm({ direction: "outbound", type: "call", at: nowLocal(), note: "" });
      toast.success("Contact logged");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log contact");
    } finally {
      setAddingContact(false);
    }
  };

  const handlePass = async () => {
    const reason = passReason.trim();
    if (!reason) {
      toast.error("Please enter a reason for passing.");
      return;
    }
    setPassing(true);
    try {
      const { data } = await apiClient.patch(`/applications/${a.id}/pass`, { passed: true, reason });
      onChanged(a.id, {
        passed: true, passed_reason: data.passed_reason, passed_by: data.passed_by,
        passed_at: data.passed_at, notes: [...notes, data.note],
      });
      setPassOpen(false);
      setPassReason("");
      toast.success("Applicant passed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not pass applicant");
    } finally {
      setPassing(false);
    }
  };

  const handleUnpass = async () => {
    try {
      const { data } = await apiClient.patch(`/applications/${a.id}/pass`, { passed: false });
      onChanged(a.id, {
        passed: false, passed_reason: null, passed_by: null, passed_at: null,
        notes: [...notes, data.note],
      });
      toast.success("Removed passed status");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  return (
    <Card data-testid={`application-row-${a.id}`} className={a.passed ? "opacity-90 border-slate-300" : ""}>
      <CardContent className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
              {a.first_name} {a.last_name}
              {a.position && (
                <Badge
                  className={`text-xs ${a.position === "mason" ? "bg-orange-600 hover:bg-orange-600" : "bg-sky-600 hover:bg-sky-600"}`}
                  data-testid={`application-position-badge-${a.id}`}
                >
                  {a.position === "mason" ? "Mason" : "Laborer"}
                  {a.position === "mason" && a.years_experience != null && ` · ${a.years_experience} yrs exp`}
                </Badge>
              )}
              <Badge
                variant="outline"
                className={`text-xs ${a.language === "es" ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-slate-300 bg-slate-50 text-slate-700"}`}
                data-testid={`application-language-badge-${a.id}`}
              >
                <Languages className="w-3 h-3 mr-1" /> {a.language === "es" ? "Spanish" : "English"}
              </Badge>
              {a.source === "manual" && (
                <Badge variant="outline" className="text-xs border-sky-300 bg-sky-50 text-sky-800" data-testid={`application-manual-badge-${a.id}`}>
                  Added by office
                </Badge>
              )}
              {a.passed && (
                <Badge variant="secondary" className="bg-slate-200 text-slate-700 text-xs" data-testid={`application-passed-badge-${a.id}`}>
                  <Ban className="w-3 h-3 mr-1" /> Passed
                </Badge>
              )}
            </h3>
            <div className="mt-1 flex flex-wrap items-center gap-4 text-sm text-slate-600">
              {a.phone && (
                <a href={phoneHref(a.phone)} className="flex items-center gap-1 hover:text-slate-900">
                  <Phone className="w-3.5 h-3.5" /> {formatPhone(a.phone)}
                </a>
              )}
              {a.email && (
                <a href={`mailto:${a.email}`} className="flex items-center gap-1 hover:text-slate-900">
                  <Mail className="w-3.5 h-3.5" /> {a.email}
                </a>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {a.created_at && (
              <Badge variant="secondary" className="text-xs">
                {new Date(a.created_at).toLocaleDateString()}
              </Badge>
            )}
            <Button
              size="sm"
              variant={a.contacted ? "default" : "outline"}
              onClick={handleToggleContacted}
              data-testid={`application-contacted-${a.id}`}
              className={a.contacted ? "bg-emerald-600 hover:bg-emerald-700" : "text-emerald-700 border-emerald-300 hover:bg-emerald-50"}
            >
              <PhoneCall className="w-4 h-4 mr-1.5" />
              {a.contacted ? "Contacted" : "Mark Contacted"}
            </Button>
            {!a.onboarding_status && !a.passed && (
              <Button size="sm" variant="outline" onClick={handleHire} data-testid={`application-hire-${a.id}`} className="text-indigo-700 border-indigo-300 hover:bg-indigo-50">
                <UserCheck className="w-4 h-4 mr-1.5" /> Hire
              </Button>
            )}
            {a.onboarding_status && (
              <Badge className="bg-indigo-600 text-xs" data-testid={`application-hired-badge-${a.id}`}>
                {a.onboarding_status === "completed" ? "Onboarded" : a.onboarding_status === "employee_completed" ? "Forms Received" : "Hired"}
              </Badge>
            )}
            {!a.onboarding_status && (
              a.passed ? (
                <Button size="sm" variant="ghost" onClick={handleUnpass} data-testid={`application-unpass-${a.id}`} className="text-slate-600 hover:bg-slate-100">
                  <RotateCcw className="w-4 h-4 mr-1.5" /> Un-pass
                </Button>
              ) : (
                <Button size="sm" variant="ghost" onClick={() => setPassOpen((v) => !v)} data-testid={`application-pass-${a.id}`} className="text-amber-700 hover:bg-amber-50">
                  <Ban className="w-4 h-4 mr-1.5" /> Pass
                </Button>
              )
            )}
            <Button size="sm" variant="ghost" onClick={() => onEdit(a)} data-testid={`application-edit-${a.id}`} className="text-slate-600 hover:bg-slate-100">
              <Pencil className="w-4 h-4" />
            </Button>
            {confirmDelete ? (
              <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 shadow-sm" data-testid={`application-confirm-block-${a.id}`}>
                <span className="text-xs font-medium text-red-700">Delete this application?</span>
                <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white font-semibold" onClick={handleDelete} data-testid={`application-confirm-delete-${a.id}`}>
                  <Trash2 className="w-4 h-4 mr-1.5" /> Yes, Delete
                </Button>
                <Button size="sm" variant="ghost" className="text-slate-600 hover:bg-slate-200" onClick={() => setConfirmDelete(false)}>Cancel</Button>
              </div>
            ) : (
              <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => setConfirmDelete(true)} data-testid={`application-delete-${a.id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Pass reason form */}
        {passOpen && !a.passed && (
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid={`application-pass-form-${a.id}`}>
            <Label className="text-sm font-medium text-amber-800">Reason for passing (required)</Label>
            <Textarea
              value={passReason}
              onChange={(e) => setPassReason(e.target.value)}
              placeholder="e.g. Position filled, didn't meet requirements, no longer interested…"
              rows={2}
              data-testid={`application-pass-reason-${a.id}`}
            />
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="ghost" onClick={() => { setPassOpen(false); setPassReason(""); }}>Cancel</Button>
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={handlePass} disabled={passing} data-testid={`application-pass-confirm-${a.id}`}>
                {passing ? "Saving…" : "Confirm Pass"}
              </Button>
            </div>
          </div>
        )}

        {a.passed && a.passed_reason && (
          <div className="mt-3 flex items-start gap-2 rounded-md bg-slate-100 border border-slate-200 px-3 py-2 text-sm text-slate-700" data-testid={`application-passed-stamp-${a.id}`}>
            <Ban className="w-4 h-4 flex-shrink-0 mt-0.5 text-slate-500" />
            <span>
              <strong>Passed</strong>{a.passed_by && <> by {a.passed_by}</>}{a.passed_at && ` on ${new Date(a.passed_at).toLocaleDateString()}`} — {a.passed_reason}
            </span>
          </div>
        )}

        {a.onboarding_status && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md bg-indigo-50 border border-indigo-200 px-3 py-2" data-testid={`application-onboarding-bar-${a.id}`}>
            <span className="text-sm text-indigo-800 mr-1">
              {a.hired_by && <>Hired by <strong>{a.hired_by}</strong> · </>}
              Onboarding: <strong>{a.onboarding_status.replace("_", " ")}</strong>
            </span>
            {a._ob?.token && a.onboarding_status === "hired" && (
              <>
                <Button size="sm" variant="outline" onClick={emailApplicantLink} data-testid={`application-email-link-${a.id}`}>
                  <Mail className="w-4 h-4 mr-1.5" /> Resend Link
                </Button>
                <Button size="sm" variant="outline" onClick={() => navigate(`/onboarding/${a._ob.token}`)} data-testid={`application-fill-inoffice-${a.id}`}>
                  <ClipboardList className="w-4 h-4 mr-1.5" /> Fill In Office
                </Button>
              </>
            )}
            {a._ob?.id && (a.onboarding_status === "employee_completed" || a.onboarding_status === "completed") && (
              <Button
                size="sm"
                className={a.onboarding_status === "employee_completed" ? "bg-indigo-600 hover:bg-indigo-700" : ""}
                variant={a.onboarding_status === "completed" ? "outline" : "default"}
                onClick={() => navigate(`/onboarding/review/${a._ob.id}`)}
                data-testid={`application-review-${a.id}`}
              >
                {a.onboarding_status === "employee_completed" ? "Complete Employer Section" : "View Packet"}
              </Button>
            )}
          </div>
        )}

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 border-t pt-3">
          {QUESTION_LABELS.map(([key, label]) => (
            <div key={key} className="flex items-center justify-between text-sm">
              <span className="text-slate-600">{label}</span>
              <YesNoBadge value={a[key] === null || a[key] === undefined ? (a.source === "manual" ? null : false) : !!a[key]} />
            </div>
          ))}
        </div>

        {/* Notes & Contact activity */}
        <div className="mt-3 border-t pt-3">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900"
            data-testid={`application-activity-toggle-${a.id}`}
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Notes &amp; Contact Log
            {(notes.length > 0 || contactLog.length > 0) && (
              <span className="text-xs text-slate-500">({notes.length} note{notes.length !== 1 ? "s" : ""}, {contactLog.length} contact{contactLog.length !== 1 ? "s" : ""})</span>
            )}
          </button>

          {expanded && (
            <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid={`application-activity-${a.id}`}>
              {/* Notes */}
              <div className="space-y-2">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  <StickyNote className="w-3.5 h-3.5" /> Notes
                </div>
                <div className="space-y-2 max-h-56 overflow-y-auto">
                  {notes.length === 0 ? (
                    <p className="text-sm text-slate-400">No notes yet.</p>
                  ) : (
                    [...notes].reverse().map((n) => (
                      <div key={n.id} className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2" data-testid={`note-item-${n.id}`}>
                        <p className="text-sm text-slate-800 whitespace-pre-wrap">{n.text}</p>
                        <p className="text-xs text-slate-400 mt-1">{n.by || "—"}{n.created_at && ` · ${new Date(n.created_at).toLocaleString()}`}</p>
                      </div>
                    ))
                  )}
                </div>
                <div className="flex gap-2">
                  <Textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Add a note…"
                    rows={2}
                    className="flex-1"
                    data-testid={`note-input-${a.id}`}
                  />
                </div>
                <Button size="sm" onClick={handleAddNote} disabled={addingNote || !noteText.trim()} className="w-full" data-testid={`note-add-${a.id}`}>
                  <Plus className="w-4 h-4 mr-1.5" /> {addingNote ? "Adding…" : "Add Note"}
                </Button>
              </div>

              {/* Contact log */}
              <div className="space-y-2">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  <PhoneCall className="w-3.5 h-3.5" /> Contact Log
                </div>
                <div className="space-y-2 max-h-56 overflow-y-auto">
                  {contactLog.length === 0 ? (
                    <p className="text-sm text-slate-400">No contact logged yet.</p>
                  ) : (
                    [...contactLog].reverse().map((c) => (
                      <div key={c.id} className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2" data-testid={`contact-item-${c.id}`}>
                        <div className="flex items-center gap-1.5 text-sm text-slate-800">
                          {c.direction === "inbound"
                            ? <PhoneIncoming className="w-3.5 h-3.5 text-blue-600" />
                            : <PhoneOutgoing className="w-3.5 h-3.5 text-emerald-600" />}
                          <span className="font-medium">{typeLabel(c.type)}</span>
                          <span className="text-slate-500">· {c.direction === "inbound" ? "They contacted us" : "We contacted them"}</span>
                        </div>
                        {c.note && <p className="text-sm text-slate-700 mt-0.5 whitespace-pre-wrap">{c.note}</p>}
                        <p className="text-xs text-slate-400 mt-1">{c.at && new Date(c.at).toLocaleString()}{c.by && ` · ${c.by}`}</p>
                      </div>
                    ))
                  )}
                </div>
                <div className="rounded-md border border-slate-200 p-2 space-y-2 bg-white">
                  <div className="grid grid-cols-2 gap-2">
                    <Select value={contactForm.direction} onValueChange={(v) => setContactForm((p) => ({ ...p, direction: v }))}>
                      <SelectTrigger data-testid={`contact-direction-${a.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="outbound">We contacted them</SelectItem>
                        <SelectItem value="inbound">They contacted us</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={contactForm.type} onValueChange={(v) => setContactForm((p) => ({ ...p, type: v }))}>
                      <SelectTrigger data-testid={`contact-type-${a.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CONTACT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Input
                    type="datetime-local"
                    value={contactForm.at}
                    onChange={(e) => setContactForm((p) => ({ ...p, at: e.target.value }))}
                    data-testid={`contact-at-${a.id}`}
                  />
                  <Input
                    value={contactForm.note}
                    onChange={(e) => setContactForm((p) => ({ ...p, note: e.target.value }))}
                    placeholder="Outcome (e.g. left voicemail)"
                    data-testid={`contact-note-${a.id}`}
                  />
                  <Button size="sm" onClick={handleAddContact} disabled={addingContact} className="w-full" data-testid={`contact-add-${a.id}`}>
                    <Plus className="w-4 h-4 mr-1.5" /> {addingContact ? "Logging…" : "Log Contact"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ApplicantCard;
